import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
} from 'reactstrap';
import { toast } from 'react-toastify';
import ActionButton from '../../../../Components/Common/ActionButton';

import * as api from './planningApi';
import { SUPPORTED_MEKANISME_NAME } from './constants';

// "Create New" popup. Jenis Pengadaan + Mekanisme Pengadaan are real lookups
// from ProcurementController (contract section 0); the backend only accepts the
// COS + "Pelelangan khusus" combination and 422s anything else, so we surface
// every option but let the server be the source of truth on the combination.
const emptyForm = {
  jenisPengadaanId: '',
  title: '',
  mekanismePengadaanId: '',
  description: '',
};

const PlanningCreateModal = ({ show, submitting = false, onClose, onSave }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [jenisOptions, setJenisOptions] = useState([]);
  const [mekanismeOptions, setMekanismeOptions] = useState([]);
  const [loadingJenis, setLoadingJenis] = useState(false);
  const [loadingMekanisme, setLoadingMekanisme] = useState(false);

  // Load Jenis Pengadaan when the modal opens; auto-select if there is only one.
  useEffect(() => {
    if (!show) return;
    setForm(emptyForm);
    setErrors({});
    setMekanismeOptions([]);
    setLoadingJenis(true);
    api
      .lookupJenisPengadaan()
      .then(items => {
        setJenisOptions(items);
        if (items.length === 1) {
          setForm(prev => ({ ...prev, jenisPengadaanId: items[0].id }));
        }
      })
      .catch(error => toast.error(`Failed to load Jenis Pengadaan: ${error}`))
      .finally(() => setLoadingJenis(false));
  }, [show]);

  // Mekanisme is filtered by the selected Jenis Pengadaan (required query param).
  useEffect(() => {
    if (!form.jenisPengadaanId) {
      setMekanismeOptions([]);
      return;
    }
    setLoadingMekanisme(true);
    api
      .lookupMekanismePengadaan(form.jenisPengadaanId)
      .then(items => {
        setMekanismeOptions(items);
        // Prefer the supported "Pelelangan khusus" row if present.
        const preferred = items.find(m => m.text === SUPPORTED_MEKANISME_NAME);
        if (preferred) {
          setForm(prev => ({ ...prev, mekanismePengadaanId: preferred.id }));
        }
      })
      .catch(error =>
        toast.error(`Failed to load Mekanisme Pengadaan: ${error}`)
      )
      .finally(() => setLoadingMekanisme(false));
  }, [form.jenisPengadaanId]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => {
      // Changing Jenis Pengadaan invalidates the previously chosen Mekanisme.
      if (name === 'jenisPengadaanId') {
        return { ...prev, jenisPengadaanId: value, mekanismePengadaanId: '' };
      }
      return { ...prev, [name]: value };
    });
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!form.jenisPengadaanId)
      next.jenisPengadaanId = 'Jenis Pengadaan is required';
    if (!form.title.trim()) next.title = 'Title procurement is required';
    if (!form.mekanismePengadaanId)
      next.mekanismePengadaanId = 'Mekanisme pengadaan is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave({
      jenisPengadaanId: form.jenisPengadaanId,
      title: form.title.trim(),
      mekanismePengadaanId: form.mekanismePengadaanId,
      description: form.description.trim(),
    });
  };

  return (
    <Modal
      isOpen={show}
      toggle={onClose}
      centered
      data-bs-backdrop='static'
      size='md'
    >
      <ModalHeader className='bg-soft-info' toggle={onClose}>
        <span className='modal-title text-info fw-bold'>
          Create New Planning
        </span>
      </ModalHeader>
      <ModalBody>
        <div className='mb-3'>
          <Label for='jenisPengadaanId' className='form-label'>
            Jenis Pengadaan <span className='text-danger'>*</span>
          </Label>
          <Input
            type='select'
            id='jenisPengadaanId'
            name='jenisPengadaanId'
            invalid={!!errors.jenisPengadaanId}
            value={form.jenisPengadaanId}
            disabled={loadingJenis}
            onChange={handleChange}
          >
            <option value=''>
              {loadingJenis ? 'Loading...' : 'Select Jenis Pengadaan'}
            </option>
            {jenisOptions.map(j => (
              <option key={j.id} value={j.id}>
                {j.text}
              </option>
            ))}
          </Input>
          {errors.jenisPengadaanId && (
            <div className='invalid-feedback d-block'>
              {errors.jenisPengadaanId}
            </div>
          )}
        </div>
        <div className='mb-3'>
          <Label for='title' className='form-label'>
            Title Procurement <span className='text-danger'>*</span>
          </Label>
          <Input
            type='text'
            id='title'
            name='title'
            maxLength={500}
            invalid={!!errors.title}
            value={form.title}
            onChange={handleChange}
          />
          {errors.title && (
            <div className='invalid-feedback d-block'>{errors.title}</div>
          )}
        </div>
        <div className='mb-3'>
          <Label for='mekanismePengadaanId' className='form-label'>
            Mekanisme Pengadaan <span className='text-danger'>*</span>
          </Label>
          <Input
            type='select'
            id='mekanismePengadaanId'
            name='mekanismePengadaanId'
            invalid={!!errors.mekanismePengadaanId}
            value={form.mekanismePengadaanId}
            disabled={!form.jenisPengadaanId || loadingMekanisme}
            onChange={handleChange}
          >
            <option value=''>
              {loadingMekanisme ? 'Loading...' : 'Select Mekanisme Pengadaan'}
            </option>
            {mekanismeOptions.map(m => (
              <option key={m.id} value={m.id}>
                {m.text}
              </option>
            ))}
          </Input>
          {errors.mekanismePengadaanId && (
            <div className='invalid-feedback d-block'>
              {errors.mekanismePengadaanId}
            </div>
          )}
        </div>
        <div className='mb-1'>
          <Label for='description' className='form-label'>
            Description
          </Label>
          <Input
            type='textarea'
            id='description'
            name='description'
            rows={3}
            maxLength={500}
            value={form.description}
            onChange={handleChange}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <ActionButton
          variant='secondary'
          size='sm'
          icon='ri-close-line'
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </ActionButton>
        <ActionButton
          variant='primary'
          size='sm'
          onClick={handleSave}
          loading={submitting}
        >
          Save
        </ActionButton>
      </ModalFooter>
    </Modal>
  );
};

export default PlanningCreateModal;
